import { useEffect, useState } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router'
import { useAuth } from './AuthContext'
import { BurbujaAsistente } from '../components/ayuda/BurbujaAsistente'
import { BeeDock } from '../components/chrome/BeeDock'
import { BeeNavbar } from '../components/chrome/BeeNavbar'
import { NetChip } from '../components/chrome/NetChip'
import { ChipActualizacion } from '../components/chrome/ChipActualizacion'
import { useNavMode } from '../lib/navMode'
import { TabBar } from '../components/tabs/TabBar'
import { useStandalone } from '../lib/install'
import { GuidedTour } from '../components/tour/GuidedTour'
import { ChatBubble, ChatBubbleProvider } from '../components/chat/bubble'
import { PhotosProvider } from '../components/photos'
import { enablePush, pushSupported } from '../lib/push'
import { esEscritorio } from '../lib/desktop'
import { useNotifsEscritorio } from '../lib/notifsEscritorio'

// Banner discreto de opt-in a push (notificaciones.md): una vez, descartable.
function PushBanner() {
  const [show, setShow] = useState(false)
  useEffect(() => {
    // En la app de escritorio no hay Web Push que ofrecer: las notificaciones
    // del sistema ya salen solas por window.desktop.notify, sin permiso que
    // pedir. pushSupported() también devuelve false ahí, pero se corta antes
    // para no pagar el import dinámico de firebase/messaging.
    if (esEscritorio()) return
    if (localStorage.getItem('bee_push_prompted')) return
    void pushSupported().then((ok) => {
      if (ok && Notification.permission === 'default') setShow(true)
    })
  }, [])
  if (!show) return null
  const dismiss = () => {
    localStorage.setItem('bee_push_prompted', '1')
    setShow(false)
  }
  return (
    <div className="push-banner">
      <span>¿Quieres recibir notificaciones aunque no tengas bee camp abierto?</span>
      <button
        className="btn btn-primary"
        onClick={() => {
          void enablePush().finally(dismiss)
        }}
      >
        Activar
      </button>
      <button className="btn btn-ghost" onClick={dismiss}>
        Ahora no
      </button>
    </div>
  )
}

// Guardia de sesión + layout con sidebar. Las rutas protegidas se anidan aquí;
// cada página pinta su propio BeeTopbar (el título varía por página).
export function RequireAuth() {
  const { user, me, loading, can } = useAuth()
  const location = useLocation()
  // standalone incluye la app de escritorio (lib/install.ts): las pestañas son
  // metáfora de app instalada y ahí lo está.
  const standalone = useStandalone()
  const navMode = useNavMode()
  // Notificaciones del sistema + navegación desde ellas. No hace nada en
  // navegador; se engancha aquí porque es el único sitio con sesión garantizada
  // y montado durante toda la vida de la app.
  useNotifsEscritorio(user?.email)

  if (loading) {
    return (
      <div className="app-content" aria-busy="true">
        <div className="skel skel-card" />
      </div>
    )
  }
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/bienvenida?next=${next}`} replace />
  }

  return (
    <PhotosProvider>
    <ChatBubbleProvider>
      {/* El sidebar ocupa una columna EN FLUJO de 208px; el dock flota. La clase
          marca el shell para que el CSS sepa cuál layout aplicar. */}
      <div className={navMode === 'sidebar' ? 'app-shell has-sidebar' : 'app-shell'}>
        <NetChip />
        <ChipActualizacion />
        {navMode === 'sidebar' ? (
          <BeeNavbar isAdmin={can('ADMIN.USUARIOS.VER')} />
        ) : (
          <BeeDock isAdmin={can('ADMIN.USUARIOS.VER')} />
        )}
        <main className="app-main">
          {/* Las pestañas son una metáfora de app instalada: en el navegador ya
              tienes las del propio navegador y salían dos filas encimadas. Se
              MONTA condicionalmente en vez de ocultarse con CSS — así el botón
              "+" no queda alcanzable con Tab ni escribe en sessionStorage. */}
          {standalone && <TabBar key={user.email} cuenta={user.email} />}
          <PushBanner />
          <Outlet />
        </main>
        <ChatBubble />
        <BurbujaAsistente />
        <GuidedTour />
      </div>
    </ChatBubbleProvider>
    </PhotosProvider>
  )
}

export function RequireAdmin() {
  const { loading, can } = useAuth()
  if (loading) return null
  // Por PERMISO, no por id de rol: ahora los roles son datos, así que un rol
  // creado a la medida con permisos de administración debe poder entrar, y un
  // 'admin' con el permiso revocado no.
  if (!can('ADMIN.USUARIOS.VER')) return <Navigate to="/" replace />
  return <Outlet />
}
